import type {
  CreateProfileRequest,
  UpdateProfileRequest,
  AddContactRequest,
  TrackFeedViewRequest,
  SetProfileActiveRequest,
  ProfileResponse,
  FeedResponse,
  ContactsResponse,
  AddContactResponse,
  InterestsResponse,
  DrivingStylesResponse,
  PhotoResponse,
  TrackViewResponse,
} from "../types/networking";

export interface FeedParams {
  limit?: number;
  offset?: number;
  age_filter?: "default" | "older" | "younger" | "same";
  sex_filter?: "all" | "male" | "female";
  city_ids?: number[];
  interest_ids?: number[];
  goal_filters?: Array<"friends" | "networking" | "adventures" | "romance">;
}

const BASE_URL = "https://dpscontrol.ru/api/v1/driver";

const getInitData = (): string => {
  return window.Telegram?.WebApp?.initData || import.meta.env.VITE_DEVELOP_MODE;
};

// Базовая функция для JSON запросов
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const method = options.method || "GET";
  const url = `${BASE_URL}${endpoint}`;

  console.log(`🌐 [API ${method}] ${endpoint}`, {
    url,
    body: options.body ? JSON.parse(options.body as string) : undefined,
  });

  const headers = {
    "Content-Type": "application/json",
    "X-Telegram-Init-Data": getInitData(),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  const data = await response.json();

  return data;
}

// Функция для multipart/form-data запросов
async function fetchMultipart<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;

  console.log(`🌐 [API POST MULTIPART] ${endpoint}`, {
    url,
    formData: Array.from(formData.entries()),
  });

  const headers = {
    "X-Telegram-Init-Data": getInitData(),
    // НЕ устанавливаем Content-Type - браузер сам установит с boundary
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    console.error(`❌ [API POST MULTIPART] ${endpoint} FAILED:`, error);
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  const data = await response.json();
  console.log(`✅ [API POST MULTIPART] ${endpoint} SUCCESS:`, data);
  return data;
}

export const networkingAPI = {
  // ========== ПРОФИЛЬ ==========

  /**
   * GET /profile - Получить профиль
   */
  async getProfile(): Promise<ProfileResponse> {
    try {
      const result = await fetchAPI<ProfileResponse>("/profile");
      console.log('_+_', result)
      return result;
    } catch (error: any) {
      // 404 - это нормально, профиль просто не создан
      if (error.message === "profile not found") {
        console.log(
          "ℹ️ [API GET] /profile - профиль не найден (это нормально)"
        );
        return { success: false, profile: null };
      }
      throw error;
    }
  },

  /**
   * POST /profile - Создать профиль
   */
  async createProfile(data: CreateProfileRequest): Promise<ProfileResponse> {
    return fetchAPI("/profile", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * PUT /profile - Обновить профиль
   */
  async updateProfile(
    data: UpdateProfileRequest
  ): Promise<{ success: boolean; message: string; profile?: any }> {
    return fetchAPI("/profile", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * DELETE /profile - Удалить профиль
   */
  async deleteProfile(): Promise<{ success: boolean; message: string }> {
    return fetchAPI("/profile", {
      method: "DELETE",
    });
  },

  /**
   * POST /profile/active - Скрыть/показать профиль
   */
  async setProfileActive(
    data: SetProfileActiveRequest
  ): Promise<{ success: boolean; message: string }> {
    return fetchAPI("/profile/active", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // ========== ИНТЕРЕСЫ ==========

  /**
   * POST /profile/interests - Добавить интересы
   */
  async addInterests(interestIds: number[]): Promise<{
    success: boolean;
    message: string;
    interests?: any[];
    profile_completion?: number;
  }> {
    return fetchAPI("/profile/interests", {
      method: "POST",
      body: JSON.stringify({ interest_ids: interestIds }),
    });
  },

  /**
   * DELETE /profile/interests/{interestID} - Удалить интерес
   */
  async removeInterest(
    interestId: number
  ): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`/profile/interests/${interestId}`, {
      method: "DELETE",
    });
  },

  // ========== ФОТО ==========

  /**
   * POST /photos - Добавить фото (multipart/form-data)
   * @param imageFile - File объект изображения
   * @param displayOrder - Порядок отображения (опционально)
   */
  async addPhoto(
    imageFile: File,
    displayOrder?: number
  ): Promise<PhotoResponse> {
    const formData = new FormData();
    formData.append("image", imageFile);
    if (displayOrder !== undefined) {
      formData.append("display_order", displayOrder.toString());
    }

    return fetchMultipart("/photos", formData);
  },

  /**
   * DELETE /photos/{photoID} - Удалить фото
   */
  async deletePhoto(
    photoId: number
  ): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`/photos/${photoId}`, {
      method: "DELETE",
    });
  },

  /**
   * POST /photos/{photoID}/primary - Установить главное фото
   */
  async setPrimaryPhoto(
    photoId: number
  ): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`/photos/${photoId}/primary`, {
      method: "POST",
    });
  },

  // ========== СПРАВОЧНИКИ (публичные) ==========

  /**
   * GET /public/driving-styles - Стили вождения
   */
  async getDrivingStyles(): Promise<DrivingStylesResponse> {
    console.log("🚗 [API] Загружаю стили вождения...");
    const response = await fetchAPI<DrivingStylesResponse>(
      "/public/driving-styles"
    );

    return response;
  },

  /**
   * GET /public/interests - Интересы
   */
  async getInterests(): Promise<InterestsResponse> {
    console.log("🎯 [API] Загружаю интересы...");
    const response = await fetchAPI<InterestsResponse>("/public/interests");
    return response;
  },

  // ========== ЛЕНТА ==========

  /**
   * GET /feed/filtered - Получить ленту с фильтрами (query params, CSV)
   */
  async getFeed(params?: FeedParams): Promise<FeedResponse> {
    const query = new URLSearchParams();
    query.set("limit", String(params?.limit ?? 30));
    query.set("offset", String(params?.offset ?? 0));
    if (params?.age_filter) query.set("age_filter", params.age_filter);
    if (params?.sex_filter) query.set("sex_filter", params.sex_filter);
    if (params?.city_ids?.length) query.set("city_ids", params.city_ids.join(","));
    if (params?.interest_ids?.length) query.set("interest_ids", params.interest_ids.join(","));
    if (params?.goal_filters?.length) query.set("goal_filters", params.goal_filters.join(","));
    return fetchAPI(`/feed/filtered?${query.toString()}`);
  },

  /**
   * POST /feed/track - Отметить просмотр
   */
  async trackView(data: TrackFeedViewRequest): Promise<TrackViewResponse> {
    return fetchAPI("/feed/track", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // ========== КОНТАКТЫ ==========

  /**
   * GET /contacts - Получить контакты
   */
  async getContacts(): Promise<ContactsResponse> {
    return fetchAPI("/contacts");
  },

  /**
   * POST /contacts - Добавить контакт
   */
  async addContact(data: AddContactRequest): Promise<AddContactResponse> {
    return fetchAPI("/contacts", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * DELETE /contacts/{profileID} - Удалить контакт
   * Также используется для отклонения входящего запроса
   */
  async deleteContact(
    profileId: number
  ): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`/contacts/${profileId}`, {
      method: "DELETE",
    });
  },
};
